// src/components/studentPortal/CreateProjectModal.jsx
// New UI wired to EXISTING, untouched backend logic (POST /projects,
// POST /addProjectMember, POST /activateTool, the 8-project/5-member free-
// tier caps) plus the one new endpoint added this session
// (GET /getUserIdByEmail). No backend changes here.
//
// Error handling (confirmed with the owner before building this):
// - POST /projects failing is the ONLY hard failure - nothing exists yet.
// - Once the project exists, every later step (creator add, tool
//   activation, each teammate) is attempted independently. A failure never
//   reverts or hides the fact that the project was created; it just adds
//   one calm, specific line to a non-alarming summary banner. The 5-member
//   cap is enforced server-side per addProjectMember call (fresh row count
//   each time - free_tier_caps.py), so a cap rejection for teammate #5+
//   surfaces as exactly this kind of soft, per-email failure line too.
import React, { useState } from 'react';
import { FiX, FiFilm, FiMic, FiMapPin, FiDollarSign, FiUserPlus } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { baseUrl } from '../../config';

const PROJECT_TYPES = [
  { value: 'short', label: 'Short Film' },
  { value: 'full', label: 'Feature Film' },
  { value: 'advertisement', label: 'Advertisement' },
];

// Generic icon cycle for whatever tools GET /getAllTools actually returns -
// not hardcoded to specific tool names, since that list is DB-driven.
const TOOL_ICONS = [FiFilm, FiDollarSign, FiMic, FiMapPin];

const CreateProjectModal = ({ onClose, onCreated }) => {
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [projectType, setProjectType] = useState('short');
  const [description, setDescription] = useState('');
  const [selectedToolId, setSelectedToolId] = useState(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberChips, setMemberChips] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [hardError, setHardError] = useState('');
  const [summary, setSummary] = useState(null); // { projectCreated: true, notes: [] }

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/getAllTools`);
        if (res.ok) setTools(await res.json());
      } catch (_) {
        // Silent - the modal still works without tool tiles, tool selection just becomes empty.
      } finally {
        setToolsLoading(false);
      }
    })();
  }, []);

  const addMemberChip = () => {
    const email = memberEmail.trim().toLowerCase();
    if (!email || memberChips.includes(email)) { setMemberEmail(''); return; }
    setMemberChips((prev) => [...prev, email]);
    setMemberEmail('');
  };

  const removeMemberChip = (email) => {
    setMemberChips((prev) => prev.filter((e) => e !== email));
  };

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    setHardError('');
    setSummary(null);

    try {
      const userRes = await fetch(`${baseUrl}/getUserById`, { headers: authHeader() });
      if (!userRes.ok) { setHardError('Something went wrong, please try again.'); setSubmitting(false); return; }
      const user = await userRes.json();
      const userId = user.user_id;

      const createRes = await fetch(`${baseUrl}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          title: title.trim(),
          project_desc: description.trim(),
          project_type: projectType,
          genre: '',
          creator_id: userId,
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        // free_tier_caps.py's _deny() sends detail as {code, message, ...},
        // not a plain string (e.g. the 8-project cap) - check both shapes.
        const detailMessage = typeof err.detail === 'string'
          ? err.detail
          : (err.detail && typeof err.detail.message === 'string' ? err.detail.message : null);
        setHardError(detailMessage || "Couldn't create the project. Please try again.");
        setSubmitting(false);
        return;
      }
      const project = await createRes.json();

      // From here on: the project is real. Nothing below is a hard failure -
      // each step is independent and only adds a note to the summary.
      const notes = [];

      try {
        const r = await fetch(`${baseUrl}/addProjectMember`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ user_id: userId, project_id: project.project_id }),
        });
        if (!r.ok) notes.push("Couldn't add you as a member - you can retry from the project page.");
      } catch (_) {
        notes.push("Couldn't add you as a member - you can retry from the project page.");
      }

      if (selectedToolId) {
        try {
          const r = await fetch(`${baseUrl}/activateTool`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify({ project_id: project.project_id, tool_id: selectedToolId }),
          });
          if (!r.ok) {
            // Surface the real backend reason instead of a generic message -
            // e.g. activate_tool_for_project's 404 "No subscription found for
            // this tool" is a legitimate, expected failure (not a bug) if
            // this account never purchased that tool.
            const err = await r.json().catch(() => ({}));
            const detail = typeof err.detail === 'string' ? err.detail : null;
            notes.push(`Couldn't activate the selected tool${detail ? ` (${detail})` : ''} - you can add it later from the project page.`);
          }
        } catch (_) {
          notes.push("Couldn't activate the selected tool - you can add it later from the project page.");
        }
      }

      for (const email of memberChips) {
        try {
          const lookupRes = await fetch(`${baseUrl}/getUserIdByEmail?email=${encodeURIComponent(email)}`);
          if (!lookupRes.ok) {
            notes.push(`Couldn't add ${email} (no account found with that email) - you can add them later.`);
            continue;
          }
          const { user_id: memberUserId } = await lookupRes.json();
          const addRes = await fetch(`${baseUrl}/addProjectMember`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify({ user_id: memberUserId, project_id: project.project_id }),
          });
          if (!addRes.ok) {
            const err = await addRes.json().catch(() => ({}));
            const reason = typeof err.detail === 'string' ? err.detail : 'the free plan member limit may have been reached';
            notes.push(`Couldn't add ${email} (${reason}) - you can add them later.`);
          }
        } catch (_) {
          notes.push(`Couldn't add ${email} - you can add them later.`);
        }
      }

      setSummary({ projectCreated: true, notes });
      onCreated(project, notes);
    } catch (_) {
      setHardError('Something went wrong, please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-slate-800">Create project</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors duration-150">
            <FiX size={20} />
          </button>
        </div>

        {summary ? (
          <div>
            <p className="text-sm text-slate-700 font-medium mb-2">Project created!</p>
            {summary.notes.length > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4">
                {summary.notes.map((n, i) => (
                  <p key={i} className="text-xs text-amber-700 mb-1 last:mb-0">{n}</p>
                ))}
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full bg-teal-600 text-white font-semibold py-2.5 rounded-xl hover:bg-teal-700 transition-colors duration-200"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Project name</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={submitting}
                className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Project type</label>
              <select
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                disabled={submitting}
                className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              >
                {PROJECT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitting}
                rows={3}
                className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tool</label>
              {toolsLoading ? (
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
                  ))}
                </div>
              ) : tools.length === 0 ? (
                <p className="text-xs text-slate-400">No tools available right now - you can add one later.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {tools.map((tool, i) => {
                    const Icon = TOOL_ICONS[i % TOOL_ICONS.length];
                    const selected = selectedToolId === tool.id;
                    return (
                      <button
                        type="button"
                        key={tool.id}
                        onClick={() => setSelectedToolId(selected ? null : tool.id)}
                        disabled={submitting}
                        title={tool.name}
                        className={`flex flex-col items-center justify-center gap-1 h-16 rounded-xl border-2 transition-all duration-150 ${
                          selected ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        <Icon size={16} />
                        <span className="text-[10px] font-medium truncate w-full text-center px-1">{tool.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Team members</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMemberChip(); } }}
                  placeholder="teammate@email.com"
                  disabled={submitting}
                  className="flex-1 text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                />
                <button
                  type="button"
                  onClick={addMemberChip}
                  disabled={submitting}
                  className="px-3 rounded-xl border border-slate-300 text-slate-500 hover:border-teal-400 hover:text-teal-600 transition-colors duration-150"
                >
                  <FiUserPlus size={16} />
                </button>
              </div>
              {memberChips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {memberChips.map((email) => (
                    <span key={email} className="flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                      {email}
                      <button type="button" onClick={() => removeMemberChip(email)} className="text-slate-400 hover:text-slate-600">
                        <FiX size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {hardError && <p className="text-sm text-red-600">{hardError}</p>}

            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="w-full bg-teal-600 text-white font-semibold py-2.5 rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors duration-200"
            >
              {submitting ? 'Creating...' : 'Create project'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default CreateProjectModal;
